import React, { Dispatch, useContext, useEffect, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useSelector } from 'react-redux';
import { fetchEnrollmentPremiumDetails } from '../../../../actions/memberPortal/enrollmentsActionCreators';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { configs } from '../../../../constants/configs/configs';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useSetSideNavDetailsFromEnrollment } from '../../../../hooks/useSetSideNavDetailsFromEnrollment';
import { useAppDispatch } from '../../utils';
import AddOrChangeEnrollmentTxnContainer from '../AddOrChangeEnrollmentTxn/AddOrChangeEnrollmentTxnContainer';
import { getEnrolleeOrderList } from '../common/enrollmentUtils';
import { EnrollmentContext, MemberMyEnrollmentProvider } from '../context/MemberMyEnrollmentProvider';
import EnrolleeTable from '../EnrollmentListing/EnrolleTable';
import Note from '../Note';
import EditEnrolleeDetails from './EditEnrolleeDetails';
import EditEnrollmentPremiumHistoryTable from './EditEnrollmentPremiumHistoryTable';
import EnrollmentActionButtons from './EnrollmentActionButtons';
import EnrollmentDate from './EnrollmentPremiumHistoryDates';
import EnrollmentPremiumHistoryTable from './EnrollmentPremiumHistoryTable';
import EnrollmentPremiumHistoryHeader from './EnrollmentRemiumHistoryHeader';
import PremiumHistoryActions from './PremiumHistoryActions';

const StyledContainer = styled.div`
    table {
        th,
        td {
            text-align: center;
        }
    }
    .shown.error--msg {
        line-height: normal;
        padding: 5px;
    }
`;

const StyleActions = styled.div`
    display: flex;
    justify-content: end;
`;

const StyledCard = styled.div`
    border: ${({ theme }) => `1px solid ${theme.gray8}`};
`;

const {
    routes: { MEMBER_ENROLLMENT_LIST },
} = configs;

const EnrollmentPremiumContainer = () => {
    const navigate = useNavigate();
    const { id, eId } = useParams();
    const dispatch: Dispatch<any> = useAppDispatch();
    const { isEnrollmentPendingOrPendCancelled, viewEnrollmentPremiumHistoryAction } = usePermissions();
    const setEnrollmentAndSideNavDetails = useSetSideNavDetailsFromEnrollment();

    const { selectedEnrollment } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);
    const { isEdit, enableContinueButton, disableContinueButton, viewAddOrChangeTxn } = useContext(EnrollmentContext);

    useEffect(() => {
        (async () => {
            if (eId && !selectedEnrollment) {
                const payload = new FormData();
                payload.append('enrollmentId', eId);
                const data = await fetchEnrollmentPremiumDetails(eId, dispatch);
                setEnrollmentAndSideNavDetails(data);
            } else if (!selectedEnrollment) {
                navigate(MEMBER_ENROLLMENT_LIST.replace(':id', id));
            }
        })();
    }, [eId]);

    const getEnrolleeDataDtoList = useMemo(() => {
        const enrolee = getEnrolleeOrderList(selectedEnrollment?.enrolleeDataDtoList);
        return enrolee;
    }, [selectedEnrollment?.enrolleeDataDtoList]);

    return (
        <ConditionalRendering isTrue={selectedEnrollment}>
            <ConditionalRendering isTrue={!viewAddOrChangeTxn}>
                <EnrollmentPremiumHistoryHeader />
                <StyledContainer className="premium-history-container">
                    <StyledCard>
                        <div className="premium-details">
                        <EnrollmentDate />
                        <ConditionalRendering isTrue={!isEdit}>
                            <EnrolleeTable enrolleeDataDtoList={getEnrolleeDataDtoList} isPremiumHistory={true} />
                                <ConditionalRendering
                                    isTrue={isEnrollmentPendingOrPendCancelled(getEnrolleeDataDtoList)}
                                >
                                <Note message={'member.enrollment_list.premiumhistory.pend.note'} />
                            </ConditionalRendering>
                            <EnrollmentPremiumHistoryTable enrollment={selectedEnrollment} />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isEdit}>
                            <EditEnrolleeDetails
                                enrolleeDataDtoList={getEnrolleeDataDtoList}
                                enrollment={selectedEnrollment}
                            />
                            <EditEnrollmentPremiumHistoryTable
                                enrollment={selectedEnrollment}
                                selectedEnrollment={selectedEnrollment}
                                enableContinueButton={enableContinueButton}
                                disableContinueButton={disableContinueButton}
                            />
                            <StyleActions>
                                <EnrollmentActionButtons />
                            </StyleActions>
                        </ConditionalRendering>
                        </div>
                        <ConditionalRendering
                            isTrue={viewEnrollmentPremiumHistoryAction(selectedEnrollment) && !isEdit}
                        >
                            <PremiumHistoryActions enrollment={selectedEnrollment} />
                        </ConditionalRendering>
                    </StyledCard>
                </StyledContainer>
            </ConditionalRendering>
            <ConditionalRendering isTrue={viewAddOrChangeTxn}>
                <AddOrChangeEnrollmentTxnContainer />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default () => (
    <MemberMyEnrollmentProvider>
        <EnrollmentPremiumContainer />
    </MemberMyEnrollmentProvider>
);
